import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { Loader2, AlertCircle, Plus, Save } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@cms/ui/components/card";
import { Button } from "@cms/ui/components/button";
import { Alert, AlertTitle, AlertDescription } from "@cms/ui/components/alert";
import { LoadingSpinner } from "@cms/ui/components/loading-spinner";
import { PageHeader } from "@cms/ui/components/page-header";
import { MENU_HINTS, menuProblem, moveItem, toSavePayload, type MenuItem } from "../lib/menus";
import { MenuItemRow, type PageOption } from "../components/nav-menu-item-row";
import type { ClientOutletContext } from "../lib/outlet-context";

interface Menu {
  slug: string;
  name: string;
  items: MenuItem[];
}

function MenuCard({ menu, pages, saving, error, onItemsChange, onSave, dirty }: {
  menu: Menu;
  pages: PageOption[];
  saving: boolean;
  error: string;
  onItemsChange: (items: MenuItem[]) => void;
  onSave: () => void;
  dirty: boolean;
}) {
  const problem = menuProblem(menu.items);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{menu.name} menu</CardTitle>
        <CardDescription>{MENU_HINTS[menu.slug] ?? "Navigation links on your site."}</CardDescription>
      </CardHeader>
      <CardContent>
        {menu.items.length === 0 && (
          <p className="py-4 text-sm text-muted-foreground">
            No links yet — this menu is hidden on your site until you add some.
          </p>
        )}
        {menu.items.map((item, index) => (
          <MenuItemRow
            key={index}
            item={item}
            index={index}
            count={menu.items.length}
            pages={pages}
            onChange={(next) => {
              const items = menu.items.slice();
              items[index] = next;
              onItemsChange(items);
            }}
            onMove={(delta) => onItemsChange(moveItem(menu.items, index, delta))}
            onRemove={() => onItemsChange(menu.items.filter((_, i) => i !== index))}
          />
        ))}
        <Button
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={() => onItemsChange([...menu.items, { label: "", url: "" }])}
        >
          <Plus className="h-3.5 w-3.5" />
          Add link
        </Button>
      </CardContent>
      <CardFooter className="justify-between gap-3">
        <p className="text-sm text-destructive">{error}</p>
        <Button onClick={onSave} disabled={!dirty || !!problem || saving} size="sm">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save {menu.name.toLowerCase()} menu
        </Button>
      </CardFooter>
    </Card>
  );
}

export function MenusPage() {
  const { api, refreshStatus } = useOutletContext<ClientOutletContext>();
  const [menus, setMenus] = useState<Menu[] | null>(null);
  const [savedJson, setSavedJson] = useState<Record<string, string>>({});
  const [pages, setPages] = useState<PageOption[]>([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState("");
  const [saveErrors, setSaveErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!api) return;
    Promise.all([
      api.request<{ menus?: Menu[] }>("/tenant/menus"),
      api.request<{ pages?: PageOption[] }>("/tenant/pages"),
    ])
      .then(([menusData, pagesData]) => {
        const loaded = menusData.menus ?? [];
        setMenus(loaded);
        setSavedJson(Object.fromEntries(loaded.map((m) => [m.slug, JSON.stringify(m.items)])));
        setPages(pagesData.pages ?? []);
      })
      .catch((err: Error) => setError(err.message));
  }, [api]);

  const setMenuItems = (slug: string, items: MenuItem[]) => {
    setMenus((prev) => (prev ? prev.map((menu) => (menu.slug === slug ? { ...menu, items } : menu)) : prev));
  };

  const saveMenu = async (menu: Menu) => {
    if (!api) return;
    setSaving(menu.slug);
    setSaveErrors((prev) => ({ ...prev, [menu.slug]: "" }));
    try {
      const result = await api.request<{ items: MenuItem[] }>(`/tenant/menus/${menu.slug}`, {
        method: "PUT",
        body: JSON.stringify({ items: toSavePayload(menu.items) }),
      });
      setMenuItems(menu.slug, result.items);
      setSavedJson((prev) => ({ ...prev, [menu.slug]: JSON.stringify(result.items) }));
      refreshStatus?.();
    } catch (err) {
      setSaveErrors((prev) => ({ ...prev, [menu.slug]: err instanceof Error ? err.message : String(err) }));
    } finally {
      setSaving("");
    }
  };

  return (
    <div>
      <PageHeader
        title="Navigation"
        description="Manage the navigation menus on your site. Links to draft pages stay hidden until the page is published."
      />

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {!menus && !error && <LoadingSpinner className="py-20" />}

      {menus && (
        <div className="space-y-6">
          {menus.map((menu) => (
            <MenuCard
              key={menu.slug}
              menu={menu}
              pages={pages}
              saving={saving === menu.slug}
              error={saveErrors[menu.slug] ?? ""}
              dirty={JSON.stringify(menu.items) !== savedJson[menu.slug]}
              onItemsChange={(items) => setMenuItems(menu.slug, items)}
              onSave={() => saveMenu(menu)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
